import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, Clock } from "lucide-react";
import { AnswerMCScreen } from "@/components/sim/AnswerMCScreen";
import { AnswerOpenTextScreen } from "@/components/sim/AnswerOpenTextScreen";
import { OutcomeScreen, type OutcomeBand } from "@/components/sim/OutcomeScreen";
import { ScreenShell, GlassCard, SimHeader, ShimmerButton } from "@/components/sim/shared";
import { VideoScreen } from "@/components/neusim/VideoScreen";
import { DocumentScreen } from "@/components/neusim/DocumentScreen";
import { useAttemptStore } from "@/store/attemptStore";
import { useAntiCheat } from "@/hooks/useAntiCheat";
import { ANTICHEAT_ENABLED } from "@/lib/config";
import type { RespondResult, TreeNode } from "@/types/simulation";

type Phase = "video" | "document" | "decision" | "scoring" | "outcome" | "timeout";

/** Map a raw 0-10 score to the new components' three-state band. */
function bandFromScore(score: number): OutcomeBand {
  if (score >= 8) return "strong";
  if (score >= 5) return "developing";
  return "needs-work";
}

function fmtTime(s: number): string {
  return `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
}

/**
 * AttemptPlayerPage — the single-page state machine that owns the live attempt.
 * Phases cycle: (video → document) → decision → scoring → outcome → loop, then
 * navigate to /results when the journey ends. Reuses the existing attemptStore
 * (data + API), useAntiCheat hook (anti-cheat L1+L2), and timer logic.
 * Visual layer = the NeuArena Lovable components (ported into src/components/sim).
 */
export function AttemptPlayerPage(): JSX.Element {
  const { simId, attemptId } = useParams<{ simId: string; attemptId: string }>();
  const navigate = useNavigate();
  const { tree, attempt, loadTree, respond, advance, timeoutAttempt, nodeById } = useAttemptStore();
  // Per-sim EnableAntiCheat. Default Yes when the tree isn't loaded yet — fail safe.
  const antiCheat = useAntiCheat(tree?.settings?.["EnableAntiCheat"] !== "No");

  const [phase, setPhase] = useState<Phase>("decision");
  const [activeNodeId, setActiveNodeId] = useState<string | null>(null);
  const [answer, setAnswer] = useState("");
  const [outcome, setOutcome] = useState<RespondResult | null>(null);
  const [choiceLabel, setChoiceLabel] = useState("");
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [rejection, setRejection] = useState<string | null>(null);
  const [rejectionCount, setRejectionCount] = useState(0);
  const [remaining, setRemaining] = useState<number | null>(null);
  const timeoutSavedRef = useRef(false);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  // Load the tree if we don't have it yet (or it's for a different sim).
  useEffect(() => {
    if (simId !== undefined) void loadTree(simId);
  }, [simId, loadTree]);

  // Hard refresh: the in-memory attempt is gone but the server still has it —
  // bounce to /intro which resumes-or-creates and lands us back here.
  useEffect(() => {
    if (tree !== null && attempt === null && simId !== undefined) {
      navigate(`/sim/${simId}/intro`, { replace: true });
    }
  }, [tree, attempt, simId, navigate]);

  // If we somehow land here on an already-completed attempt, jump straight to
  // the results scorecard rather than getting stuck on a phantom decision.
  useEffect(() => {
    if (attempt !== null && attempt.status !== "in_progress" && simId !== undefined && attemptId !== undefined) {
      navigate(`/sim/${simId}/attempt/${attemptId}/results`, { replace: true });
    }
  }, [attempt, simId, attemptId, navigate]);

  // First-time entry phase for a node: Video (if any) → Document (if any) → Decision.
  const entryPhaseFor = (nodeId: string | null): Phase => {
    const n = nodeById(nodeId);
    if (n === null) return "decision";
    if (n.videoUrl !== null) return "video";
    if (n.documentUrl !== null) return "document";
    return "decision";
  };

  // Initialise the displayed node once the tree + attempt are loaded. After
  // that we advance it locally so the outcome screen doesn't show the *next*
  // node's content.
  useEffect(() => {
    if (tree !== null && activeNodeId === null && attempt?.currentNodeId != null) {
      const nid = attempt.currentNodeId;
      setActiveNodeId(nid);
      setPhase(entryPhaseFor(nid));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tree, attempt, activeNodeId]);

  // Whole-attempt countdown (only if the sim enables a timer).
  const timerEnabled = tree?.settings["TimerEnabled"] === "Yes";
  const timerMinutes = Number(tree?.settings["TimerMinutes"] ?? 0);
  useEffect(() => {
    if (!timerEnabled || attempt?.startedAt === undefined || timerMinutes <= 0) return;
    const total = timerMinutes * 60;
    const startMs = new Date(attempt.startedAt).getTime();
    const tick = (): void => {
      const elapsed = Math.floor((Date.now() - startMs) / 1000);
      const rem = Math.max(0, total - elapsed);
      setRemaining(rem);
      if (rem <= 0) setPhase((p) => (p === "timeout" ? p : "timeout"));
    };
    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [timerEnabled, timerMinutes, attempt?.startedAt]);

  // When the timer elapses, close the attempt out server-side once.
  useEffect(() => {
    if (phase === "timeout" && !timeoutSavedRef.current && attemptId !== undefined) {
      timeoutSavedRef.current = true;
      void timeoutAttempt(attemptId);
    }
  }, [phase, attemptId, timeoutAttempt]);

  // Wire the anti-cheat keystroke counter onto the live textarea via ref.
  // The new component doesn't accept onKeyDown directly, so we attach here.
  useEffect(() => {
    const el = textareaRef.current;
    if (el === null || phase !== "decision") return;
    const handler = (e: KeyboardEvent): void => {
      if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
        // Synthesise a minimal React-shaped event for the hook.
        antiCheat.textareaHandlers.onKeyDown({ key: e.key, ctrlKey: e.ctrlKey, metaKey: e.metaKey, altKey: e.altKey } as React.KeyboardEvent);
      }
    };
    el.addEventListener("keydown", handler);
    return () => el.removeEventListener("keydown", handler);
  }, [phase, activeNodeId, antiCheat.textareaHandlers]);

  const currentNode: TreeNode | null = nodeById(activeNodeId);
  const isMC = currentNode?.questionType === "MultipleChoice";
  // Open-text min length: per-node override → sim-wide DefaultMinOpenTextLength → 100.
  const simDefaultMin = Math.max(0, Number(tree?.settings["DefaultMinOpenTextLength"] ?? 100) || 100);
  const minChars = currentNode?.minOpenTextLength ?? simDefaultMin;
  const totalDecisions = tree?.simulation.nodeCount ?? 1;
  const sequenceNo = currentNode?.sequenceNo ?? 1;
  const competencyName = tree?.simulation.competencyName ?? "Leadership";

  const mcOptions = useMemo(
    () => (currentNode?.choices ?? []).map((c) => ({ letter: c.letter, text: c.choiceText })),
    [currentNode],
  );

  const timerDisplay = remaining === null
    ? undefined
    : timerEnabled
      ? fmtTime(remaining)
      : undefined;

  /** Submit handler shared by OT + MC paths. */
  const submitResponse = async (kind: "mc" | "open", payload: { choiceId?: string; answerText?: string; label: string }): Promise<void> => {
    if (currentNode === null || attemptId === undefined) return;
    setSubmitError(null);
    setRejection(null);
    setChoiceLabel(payload.label);
    setPhase("scoring");
    try {
      const result = await respond({
        attemptId,
        nodeId: currentNode.id,
        choiceId: kind === "mc" ? payload.choiceId : undefined,
        answerText: kind === "open" ? payload.answerText : undefined,
        rejectionCount,
        antiCheat: antiCheat.collect(),
      });

      if (result.rejected) {
        setRejection(result.rejectionReason);
        setRejectionCount((n) => n + 1);
        setPhase("decision");
        return;
      }

      setOutcome(result);
      setPhase("outcome");
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : "Submission failed");
      setPhase("decision");
    }
  };

  const handleMcSubmit = (letter: string): void => {
    const idx = letter.charCodeAt(0) - "A".charCodeAt(0);
    const choice = currentNode?.choices?.[idx];
    if (choice === undefined) return;
    void submitResponse("mc", { choiceId: choice.id, label: `${letter} — ${choice.choiceText}` });
  };

  const handleOpenSubmit = (text: string): void => {
    setAnswer(text);
    void submitResponse("open", { answerText: text, label: text.length > 200 ? `${text.slice(0, 200)}…` : text });
  };

  const handleContinue = (): void => {
    if (outcome === null) return;
    if (outcome.completed) {
      navigate(`/sim/${simId}/attempt/${attemptId}/results`);
      return;
    }
    const nextId = outcome.nextNodeId;
    setActiveNodeId(nextId);
    setAnswer("");
    setOutcome(null);
    setChoiceLabel("");
    setRejection(null);
    setRejectionCount(0);
    antiCheat.reset();
    setPhase(entryPhaseFor(nextId));
  };

  const handleMediaContinue = async (): Promise<void> => {
    if (currentNode === null) return;
    // Video → Document chain first (a node can have BOTH).
    if (phase === "video" && currentNode.documentUrl !== null) {
      setPhase("document");
      return;
    }
    // Informational video (no question) → skip the decision and auto-advance
    // server-side. Only triggers for nodes that actually have a video URL —
    // document-only nodes still go to the decision phase.
    const isInfoVideo =
      currentNode.videoUrl !== null && currentNode.videoHasQuestion === false;
    if (isInfoVideo && attemptId !== undefined) {
      try {
        const result = await advance({ attemptId, nodeId: currentNode.id });
        if (result.completed) {
          navigate(`/sim/${simId}/attempt/${attemptId}/results`);
          return;
        }
        const nextId = result.nextNodeId;
        setActiveNodeId(nextId);
        setAnswer("");
        setOutcome(null);
        setChoiceLabel("");
        setRejection(null);
        setRejectionCount(0);
        antiCheat.reset();
        setPhase(entryPhaseFor(nextId));
        return;
      } catch (err) {
        // If advance fails, fall through to the decision phase so the learner
        // is never stuck on the video screen.
        setSubmitError(err instanceof Error ? err.message : "Couldn't advance — please try the decision below.");
      }
    }
    setPhase("decision");
  };

  // Combine AI feedback + the canonical consequence text into one block for
  // the OutcomeScreen, since it accepts a single consequence field.
  const consequenceBlock = (o: RespondResult): string => {
    const parts: string[] = [];
    if (o.feedback !== "") parts.push(o.feedback);
    if (o.consequenceText !== null && o.consequenceText !== "") parts.push(`What strong leaders do: ${o.consequenceText}`);
    return parts.length > 0 ? parts.join("\n\n") : "Outcome processed.";
  };

  return (
    <div
      className={`bg-neusim-bg min-h-screen text-white ${ANTICHEAT_ENABLED ? "select-none" : ""}`}
      onCopy={(e) => { if (ANTICHEAT_ENABLED) e.preventDefault(); }}
      onCut={(e) => { if (ANTICHEAT_ENABLED) e.preventDefault(); }}
      onContextMenu={(e) => { if (ANTICHEAT_ENABLED && phase === "decision") e.preventDefault(); }}
      onDragStart={(e) => { if (ANTICHEAT_ENABLED) e.preventDefault(); }}
    >
      <AnimatePresence mode="wait">
        {/* ── VIDEO step ── */}
        {phase === "video" && currentNode !== null && currentNode.videoUrl !== null && (
          <VideoScreen
            key="video"
            sequenceNo={sequenceNo}
            totalDecisions={totalDecisions}
            xp={attempt?.totalXp ?? 0}
            timerDisplay={timerDisplay}
            videoUrl={currentNode.videoUrl}
            videoTitle={currentNode.videoTitle}
            videoInstruction={currentNode.videoInstruction}
            onContinue={() => { void handleMediaContinue(); }}
          />
        )}

        {/* ── DOCUMENT step ── */}
        {phase === "document" && currentNode !== null && currentNode.documentUrl !== null && (
          <DocumentScreen
            key="document"
            sequenceNo={sequenceNo}
            totalDecisions={totalDecisions}
            xp={attempt?.totalXp ?? 0}
            timerDisplay={timerDisplay}
            documentUrl={currentNode.documentUrl}
            documentTitle={currentNode.documentTitle}
            onContinue={() => { void handleMediaContinue(); }}
          />
        )}

        {/* ── DECISION + SCORING — Multiple Choice ── */}
        {(phase === "decision" || phase === "scoring") && currentNode !== null && isMC && (
          <AnswerMCScreen
            key="mc"
            data={{
              decisionNumber: sequenceNo,
              totalDecisions,
              type: "Multiple Choice",
              competency: competencyName,
              scenario: currentNode.scenarioText,
              options: mcOptions,
            }}
            xp={attempt?.totalXp ?? 0}
            timerDisplay={timerDisplay}
            scoring={phase === "scoring"}
            error={submitError}
            onSubmit={handleMcSubmit}
          />
        )}

        {/* ── DECISION + SCORING — Open Text ── */}
        {(phase === "decision" || phase === "scoring") && currentNode !== null && !isMC && (
          <AnswerOpenTextScreen
            key="open"
            data={{
              decisionNumber: sequenceNo,
              totalDecisions,
              type: "Open Response",
              competency: competencyName,
              scenario: currentNode.scenarioText,
              prompt: "Your Response",
              helper: "Be specific — what would you actually say and do?",
              minChars,
            }}
            xp={attempt?.totalXp ?? 0}
            timerDisplay={timerDisplay}
            initialValue={answer}
            textareaRef={textareaRef}
            scoring={phase === "scoring"}
            error={submitError}
            blockMsg={antiCheat.pasteWarning ? "Pasting is disabled — please type your own answer." : null}
            rejectionMessage={rejection}
            onValueChange={(v) => {
              setAnswer(v);
              antiCheat.noteChange(v.length);
            }}
            onPaste={antiCheat.textareaHandlers.onPaste}
            onCopy={antiCheat.textareaHandlers.onCopy}
            onCut={antiCheat.textareaHandlers.onCut}
            onDrop={antiCheat.textareaHandlers.onDrop}
            onContextMenu={antiCheat.textareaHandlers.onContextMenu}
            onSubmit={handleOpenSubmit}
          />
        )}

        {/* ── OUTCOME ── */}
        {phase === "outcome" && outcome !== null && currentNode !== null && (
          <OutcomeScreen
            key="outcome"
            data={{
              decisionNumber: sequenceNo,
              totalDecisions,
              band: bandFromScore(outcome.score),
              scorePct: (outcome.score / 10) * 100,
              choiceLabel: choiceLabel || "Your response",
              consequence: consequenceBlock(outcome),
              isAiWritten: outcome.isAiWritten,
              isLast: outcome.completed,
            }}
            xp={attempt?.totalXp ?? 0}
            timerDisplay={timerDisplay}
            onContinue={handleContinue}
          />
        )}

        {/* ── TIMEOUT — route into the end-of-attempt flow via Results ── */}
        {phase === "timeout" && (
          <ScreenShell key="timeout">
            <SimHeader decision={sequenceNo} xp={attempt?.totalXp ?? 0} timerDisplay="00:00" />
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              className="mx-auto max-w-xl px-4 py-16 text-center"
            >
              <GlassCard className="p-10">
                <Clock className="mx-auto h-12 w-12 text-[#f87272]" />
                <h1 className="mt-5 text-3xl font-bold">Time&rsquo;s up</h1>
                <p className="mt-2 text-sm leading-relaxed text-white/70">
                  The {timerMinutes}-minute limit for this simulation has elapsed. Let&rsquo;s look at how you did on
                  the decisions you completed.
                </p>
                <div className="mt-7 flex justify-center">
                  <ShimmerButton onClick={() => navigate(`/sim/${simId}/attempt/${attemptId}/results`)}>
                    See your results <ArrowRight className="h-4 w-4" />
                  </ShimmerButton>
                </div>
              </GlassCard>
            </motion.div>
          </ScreenShell>
        )}
      </AnimatePresence>
    </div>
  );
}

